import { c as _c } from "react/compiler-runtime";
import SceneLogo from "@assets/logo/SCENE_logo.svg?react";
import SphereXgLogo from "@assets/logo/sphere-xg-logo.svg?react";
import { isSeatLocked, isSeatSoldOut } from "@components/table/members/assign-seats/assign-seats-utils";
import { SeatUsageText } from "@components/table/members/assign-seats/seat-usage-text";
import { Checkbox, InfoCircleFillIcon, Text } from "@flat-ui";
import { Stack } from "@mui-wrapper";
import { MEMBER_SEAT_DESCRIPTIONS, MEMBER_SEAT_LABELS } from "@utils/member-seats-utils";
/** The logo of each seat, as the members table shows it */
const SEAT_LOGOS = {
  xgSeat: SphereXgLogo,
  sceneSeat: SceneLogo
};

/** The size of the seat logos, which matches the logos in the members table */
const LOGO_SIZE = "24px";

/** The opacity of the logo of a seat the member can neither get nor holds */
const FADED_LOGO_OPACITY = 0.5;

/** The size of the icons in front of the status of a seat, matching the line of text they lead */
const STATUS_ICON_SX = {
  width: "1rem",
  height: "1rem",
  flexShrink: 0
};
/**
 * A status line of a seat, e.g. why it cannot be changed. It leads with an icon and is set in the
 * primary text color, so that it does not read as part of the description above it.
 */
function SeatStatus(t0) {
  const $ = _c(7);
  const {
    icon,
    children
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      gap: 0.5,
      alignItems: "center",
      mt: 0.5
    };
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  let t2;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = {
      color: "text.primary"
    };
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  let t3;
  if ($[2] !== children) {
    t3 = <Text variant="bodyS" component="span" sx={t2}>{children}</Text>;
    $[2] = children;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  let t4;
  if ($[4] !== icon || $[5] !== t3) {
    t4 = <Stack direction="row" component="span" sx={t1}>{icon}{t3}</Stack>;
    $[4] = icon;
    $[5] = t3;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  return t4;
}
/** Shows why a seat the member does not hold cannot be given to them */
function SeatCheckboxStatus(t0) {
  const $ = _c(3);
  const {
    option
  } = t0;
  if (option.isHeld || !option.blockReason || isSeatSoldOut(option)) {
    return null;
  }
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = <InfoCircleFillIcon sx={{
      ...STATUS_ICON_SX,
      color: "text.secondary"
    }} />;
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  let t2;
  if ($[1] !== option.blockReason) {
    t2 = <SeatStatus icon={t1}>{option.blockReason}</SeatStatus>;
    $[1] = option.blockReason;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  return t2;
}

/**
 * A seat to select, shown as a card with its logo and what it lets the member do, and in its corner
 * whether the member holds it or what changing it does to the seats of the workspace. A checked
 * seat is outlined in the primary color. A seat whose state cannot be changed is shaded, and tells
 * why unless the member simply holds it already.
 */
export function SeatCheckbox(t0) {
  const $ = _c(45);
  const {
    option,
    isChecked,
    seatsAvailable,
    onChange
  } = t0;
  const Logo = SEAT_LOGOS[option.seat];
  let t1;
  if ($[0] !== option) {
    t1 = isSeatLocked(option);
    $[0] = option;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const isDisabled = t1;
  let t2;
  if ($[2] !== isChecked || $[3] !== onChange || $[4] !== option.seat) {
    t2 = () => onChange(option.seat, !isChecked);
    $[2] = isChecked;
    $[3] = onChange;
    $[4] = option.seat;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  const t3 = isChecked ? "primary.main" : "divider";
  const t4 = isDisabled ? "action.hover" : "transparent";
  let t5;
  if ($[6] !== t3 || $[7] !== t4) {
    t5 = {
      alignItems: "flex-start",
      m: 0,
      p: 1.5,
      border: 1,
      borderRadius: 1,
      borderColor: t3,
      backgroundColor: t4
    };
    $[6] = t3;
    $[7] = t4;
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  let t6;
  if ($[9] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = {
      gap: 1.5,
      pt: 0.25,
      width: "100%"
    };
    $[9] = t6;
  } else {
    t6 = $[9];
  }
  const t7 = isDisabled && !option.isHeld ? FADED_LOGO_OPACITY : 1;
  let t8;
  if ($[10] !== t7) {
    t8 = {
      display: "block",
      flexShrink: 0,
      opacity: t7
    };
    $[10] = t7;
    $[11] = t8;
  } else {
    t8 = $[11];
  }
  let t9;
  if ($[12] !== Logo || $[13] !== t8) {
    t9 = <Logo height={LOGO_SIZE} width={LOGO_SIZE} aria-hidden={true} style={t8} />;
    $[12] = Logo;
    $[13] = t8;
    $[14] = t9;
  } else {
    t9 = $[14];
  }
  let t10;
  if ($[15] === Symbol.for("react.memo_cache_sentinel")) {
    t10 = {
      flex: 1,
      minWidth: 0
    };
    $[15] = t10;
  } else {
    t10 = $[15];
  }
  let t11;
  if ($[16] === Symbol.for("react.memo_cache_sentinel")) {
    t11 = {
      gap: 1,
      alignItems: "center",
      justifyContent: "space-between"
    };
    $[16] = t11;
  } else {
    t11 = $[16];
  }
  let t12;
  if ($[17] === Symbol.for("react.memo_cache_sentinel")) {
    t12 = {
      lineHeight: LOGO_SIZE
    };
    $[17] = t12;
  } else {
    t12 = $[17];
  }
  const t13 = MEMBER_SEAT_LABELS[option.seat];
  let t14;
  if ($[18] !== t13) {
    t14 = <Text variant="heading14" component="span" sx={t12}>{t13} seat</Text>;
    $[18] = t13;
    $[19] = t14;
  } else {
    t14 = $[19];
  }
  let t15;
  if ($[20] !== isChecked || $[21] !== option || $[22] !== seatsAvailable) {
    t15 = <SeatUsageText option={option} isChecked={isChecked} seatsAvailable={seatsAvailable} />;
    $[20] = isChecked;
    $[21] = option;
    $[22] = seatsAvailable;
    $[23] = t15;
  } else {
    t15 = $[23];
  }
  let t16;
  if ($[24] !== t14 || $[25] !== t15) {
    t16 = <Stack direction="row" component="span" sx={t11}>{t14}{t15}</Stack>;
    $[24] = t14;
    $[25] = t15;
    $[26] = t16;
  } else {
    t16 = $[26];
  }
  let t17;
  if ($[27] === Symbol.for("react.memo_cache_sentinel")) {
    t17 = {
      color: "text.secondary"
    };
    $[27] = t17;
  } else {
    t17 = $[27];
  }
  const t18 = MEMBER_SEAT_DESCRIPTIONS[option.seat];
  let t19;
  if ($[28] !== t18) {
    t19 = <Text variant="bodyS" component="span" sx={t17}>{t18}</Text>;
    $[28] = t18;
    $[29] = t19;
  } else {
    t19 = $[29];
  }
  let t20;
  if ($[30] !== option) {
    t20 = <SeatCheckboxStatus option={option} />;
    $[30] = option;
    $[31] = t20;
  } else {
    t20 = $[31];
  }
  let t21;
  if ($[32] !== t16 || $[33] !== t19 || $[34] !== t20) {
    t21 = <Stack component="span" sx={t10}>{t16}{t19}{t20}</Stack>;
    $[32] = t16;
    $[33] = t19;
    $[34] = t20;
    $[35] = t21;
  } else {
    t21 = $[35];
  }
  let t22;
  if ($[36] !== t21 || $[37] !== t9) {
    t22 = <Stack direction="row" component="span" sx={t6}>{t9}{t21}</Stack>;
    $[36] = t21;
    $[37] = t9;
    $[38] = t22;
  } else {
    t22 = $[38];
  }
  let t23;
  if ($[39] !== isChecked || $[40] !== isDisabled || $[41] !== t2 || $[42] !== t22 || $[43] !== t5) {
    t23 = <Checkbox checked={isChecked} disabled={isDisabled} onChange={t2} fullWidth={true} controlSx={t5} label={t22} />;
    $[39] = isChecked;
    $[40] = isDisabled;
    $[41] = t2;
    $[42] = t22;
    $[43] = t5;
    $[44] = t23;
  } else {
    t23 = $[44];
  }
  return t23;
}